import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addDoubtAnswer,
  blockDoubt,
  createDoubt,
  editDoubt,
  editDoubtAnswer,
  fetchDoubtDetail,
  fetchDoubtReportReasons,
  fetchDoubtTagsAndSubjects,
  fetchDoubts,
  markBestDoubtSolution,
  replyToDoubtAnswer,
  reportDoubt,
  toggleDoubtBookmark,
  toggleDoubtUpvote,
  toggleMyDoubt,
  toggleReplyUpvote,
} from '../services/doubtsService';

const firstArray = (...values) => values.find(Array.isArray) || [];

function paginationFor(payload = {}) {
  const pagination = payload?.pagination || payload?.page_data || {};
  return {
    currentPage: Number(pagination?.currentPage || pagination?.current_page || payload?.current_page || 1),
    totalPages: Number(pagination?.totalPages || pagination?.total_pages || payload?.total_pages || 1),
    totalRecords: Number(pagination?.totalRecords || pagination?.total_records || payload?.total_records || 0),
  };
}

function imageList(item = {}) {
  const rawImages = firstArray(item?.images, item?.doubt_images, item?.image_list, item?.attachments);
  return rawImages
    .map((image) => typeof image === 'string' ? image : image?.image_url || image?.attachment_url || image?.url || image?.file_url || image?.path || '')
    .filter(Boolean);
}

export function normalizeDoubt(item = {}, index = 0) {
  return {
    id: Number(item?.doubt_id || item?.id || index + 1),
    authorName: item?.user?.name || item?.user_name || item?.name || item?.created_by_name || 'Learner',
    authorImage: item?.user?.avatar || item?.user_image || item?.profile_image || item?.profile_pic || '',
    createdAt: item?.created_at_label || item?.created_at_human || item?.created_at || item?.time_ago || '',
    subjectName: firstArray(item?.subjects).map((subject) => subject?.subject_name || subject?.name || '').filter(Boolean).join(', ') || item?.subject_name || item?.module_name || item?.subject || '',
    topicNames: firstArray(item?.topics).map((topic) => topic?.topic_name || topic?.name || '').filter(Boolean),
    tags: firstArray(item?.tags).map((tag) => tag?.tag_name || tag?.name || '').filter(Boolean),
    title: item?.doubt_title || item?.title || item?.question || item?.doubt_name || 'Doubt',
    description: item?.doubt_description || item?.description || item?.detail || item?.question_detail || '',
    sourceModule: item?.source_module || item?.source_type || item?.doubt_source || '',
    images: imageList(item),
    answerCount: Number(item?.total_answers || item?.answers_count || item?.answer_count || item?.answers || 0),
    upvoteCount: Number(item?.total_upvotes || item?.upvotes_count || item?.upvote_count || item?.upvotes || 0),
    bookmarkStatus: Number(item?.is_bookmarked ?? item?.bookmark_status ?? 0) === 1,
    isUpvoted: Number(item?.is_upvoted || 0) === 1,
    isSolved: Number(item?.is_solved || 0) === 1,
    reportCount: Number(item?.reports_count || item?.total_reports || 0),
    isReported: Number(item?.is_reported || 0) === 1,
    ownerUserId: Number(item?.user?.user_id || item?.user_id || 0),
    canEdit: Number(item?.can_edit || item?.canEdit || 0) === 1,
    isAddedToMyDoubts: Number(item?.is_added_to_my_doubts || 0) === 1,
    raw: item,
  };
}

function normalizeSubject(item = {}, index = 0) {
  const rawTopics = firstArray(item?.topics, item?.topic_list, item?.topics_list, item?.sub_topics);
  return {
    id: Number(item?.subject_id || item?.id || index + 1),
    name: item?.subject_name || item?.name || 'Subject',
    topics: rawTopics.map((topic, topicIndex) => ({
      id: Number(topic?.topic_id || topic?.id || topicIndex + 1),
      name: topic?.topic_name || topic?.name || 'Topic',
    })),
  };
}

function normalizeTag(item = {}, index = 0) {
  return {
    id: Number(item?.tag_category_id || item?.tag_id || item?.id || index + 1),
    name: item?.tag_category_name || item?.tag_name || item?.name || 'Tag',
  };
}

export function useDoubtsList({ filterType = 'all', subjectIds = [], topicIds = [], tagCategoryIds = [], language = 'EN', enabled = true } = {}) {
  const filterKey = {
    filterType,
    subjectIds: subjectIds.map(Number).filter(Boolean).sort((left, right) => left - right),
    topicIds: topicIds.map(Number).filter(Boolean).sort((left, right) => left - right),
    tagCategoryIds: tagCategoryIds.map(Number).filter(Boolean).sort((left, right) => left - right),
    language,
  };

  return useInfiniteQuery({
    queryKey: ['doubts', filterKey],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchDoubts({ ...filterKey, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const pagination = paginationFor(lastPage);
      return pagination.currentPage < pagination.totalPages ? pagination.currentPage + 1 : undefined;
    },
    enabled,
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  });
}

export function useDoubtFilters(categoryId, language = 'EN', enabled = true, isAllDoubts = 1) {
  return useQuery({
    queryKey: ['doubtFilters', Number(categoryId), language, Number(isAllDoubts)],
    queryFn: async () => {
      const payload = await fetchDoubtTagsAndSubjects(categoryId, isAllDoubts);
      const subjects = firstArray(payload?.subjects, payload?.subject_list, payload?.subjects_list, payload?.filter_subjects, payload?.subject_data);
      const tags = firstArray(payload?.tags, payload?.tag_list, payload?.tag_categories, payload?.tag_category_list, payload?.other_tags, payload?.tag_data);
      return { subjects: subjects.map(normalizeSubject), tags: tags.map(normalizeTag) };
    },
    enabled: enabled && Number(categoryId) > 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveDoubt() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['doubts'] });
  const create = useMutation({ mutationFn: createDoubt, onSuccess: refresh });
  const edit = useMutation({ mutationFn: editDoubt, onSuccess: refresh });
  return { create, edit };
}

export function useDoubtReportReasons(language = 'EN', enabled = true) {
  return useQuery({
    queryKey: ['doubtReportReasons', language],
    queryFn: fetchDoubtReportReasons,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function flattenDoubtPages(data) {
  return (data?.pages || []).flatMap((page) => {
    const records = firstArray(page?.doubts, page?.doubt_list, page?.doubts_list, page?.doubts_data, page?.data, page?.list);
    return records.map(normalizeDoubt);
  });
}

function normalizeReply(item = {}, index = 0, level = 1) {
  const replies = firstArray(item?.replies, item?.children, item?.reply_list, item?.sub_replies);
  return {
    id: Number(item?.reply_id || item?.answer_id || item?.id || index + 1),
    authorName: item?.user?.name || item?.user_name || item?.name || 'Learner',
    authorImage: item?.user?.avatar || item?.user_image || item?.profile_image || '',
    content: item?.content || item?.answer || item?.reply || item?.description || '',
    createdAt: item?.created_at_label || item?.created_at_human || item?.created_at || item?.time_ago || '',
    upvoteCount: Number(item?.upvotes || item?.upvotes_count || item?.total_upvotes || 0),
    isUpvoted: Number(item?.is_upvoted || 0) === 1,
    repliesCount: Number(item?.replies_count || item?.reply_count || replies.length || 0),
    isBest: Number(item?.is_best_solution || item?.is_best || 0) === 1,
    canEdit: Number(item?.can_edit || item?.canEdit || 0) === 1,
    attachments: imageList(item),
    level,
    replies: replies.map((reply, replyIndex) => normalizeReply(reply, replyIndex, level + 1)),
    raw: item,
  };
}

export function useDoubtDetail(doubtId, language = 'EN') {
  return useQuery({
    queryKey: ['doubtDetail', Number(doubtId), language],
    queryFn: async () => {
      const payload = await fetchDoubtDetail(doubtId);
      const rawDoubt = payload?.doubt || payload?.doubt_detail || payload?.data || payload;
      const replies = firstArray(rawDoubt?.answers, rawDoubt?.replies, rawDoubt?.answer_list, payload?.answers, payload?.replies);
      return {
        ...normalizeDoubt(rawDoubt),
        ownerUserId: Number(rawDoubt?.user?.user_id || rawDoubt?.user_id || 0),
        canEdit: Number(rawDoubt?.can_edit || rawDoubt?.canEdit || 0) === 1,
        isAddedToMyDoubts: Number(rawDoubt?.is_added_to_my_doubts || 0) === 1,
        answers: replies.map((reply, index) => normalizeReply(reply, index)),
      };
    },
    enabled: Number(doubtId) > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useDoubtActions(doubtId, language = 'EN') {
  const queryClient = useQueryClient();
  const updateDoubt = (payload = {}) => {
    const result = payload?.doubt || payload?.reply || payload?.data || payload;
    const isUpvoted = result?.is_upvoted;
    const totalUpvotes = result?.total_upvotes ?? result?.upvotes ?? result?.upvote_count;
    const bookmarkStatus = result?.bookmark_status ?? result?.is_bookmarked;
    
    queryClient.setQueryData(['doubtDetail', Number(doubtId), language], (current) => {
      if (!current) return current;
      const newUpvoteCount = totalUpvotes ?? (isUpvoted != null ? current.upvoteCount + (Number(isUpvoted) === 1 ? 1 : -1) : current.upvoteCount);
      const computedUpvotes = Math.max(0, newUpvoteCount);
      return {
        ...current,
        ...(isUpvoted != null ? { isUpvoted: Number(isUpvoted) === 1, upvoteCount: computedUpvotes, raw: { ...current.raw, is_upvoted: isUpvoted, upvotes: computedUpvotes } } : {}),
        ...(bookmarkStatus != null ? { bookmarkStatus: Number(bookmarkStatus) === 1, raw: { ...current.raw, is_bookmarked: bookmarkStatus } } : {}),
      };
    });
    
    queryClient.setQueriesData({ queryKey: ['doubts'] }, (current) => {
      if (!current) return current;
      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          doubts: (page?.doubts || []).map((item) => {
            if (Number(item?.doubt_id || item?.id) === Number(doubtId)) {
              const currentItemUpvotes = Number(item?.total_upvotes || item?.upvotes_count || item?.upvote_count || item?.upvotes || 0);
              const newUpvoteCount = totalUpvotes ?? (isUpvoted != null ? currentItemUpvotes + (Number(isUpvoted) === 1 ? 1 : -1) : currentItemUpvotes);
              const computedUpvotes = Math.max(0, newUpvoteCount);
              return { 
                ...item, 
                ...(isUpvoted != null ? { is_upvoted: isUpvoted, upvotes: computedUpvotes } : {}), 
                ...(bookmarkStatus != null ? { is_bookmarked: bookmarkStatus } : {}) 
              };
            }
            return item;
          })
        })),
      };
    });
  };
  const updateReply = (replyId, payload = {}) => {
    const result = payload?.reply || payload?.data || payload;
    const totalUpvotes = result?.total_upvotes ?? result?.upvotes ?? result?.upvote_count;
    const visit = (reply) => Number(reply.id) === Number(replyId) ? { ...reply, isUpvoted: Number(result?.is_upvoted) === 1, upvoteCount: Number(totalUpvotes ?? reply.upvoteCount), raw: { ...reply.raw, is_upvoted: result?.is_upvoted, upvotes: totalUpvotes ?? reply.raw?.upvotes } } : { ...reply, replies: (reply.replies || []).map(visit) };
    queryClient.setQueryData(['doubtDetail', Number(doubtId), language], (current) => current ? { ...current, answers: (current.answers || []).map(visit) } : current);
  };
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['doubtDetail', Number(doubtId), language] }),
    queryClient.invalidateQueries({ queryKey: ['doubts'] }),
  ]);
  const upvoteDoubt = useMutation({ mutationFn: () => toggleDoubtUpvote(doubtId), onSuccess: (payload) => { updateDoubt(payload); return refresh(); } });
  const bookmarkDoubt = useMutation({ mutationFn: () => toggleDoubtBookmark(doubtId), onSuccess: (payload) => { updateDoubt(payload); return refresh(); } });
  const toggleMyDoubtMutation = useMutation({ mutationFn: () => toggleMyDoubt(doubtId), onSuccess: refresh });
  const blockDoubtMutation = useMutation({ mutationFn: () => blockDoubt(doubtId), onSuccess: refresh });
  const upvoteReply = useMutation({ mutationFn: ({ replyId }) => toggleReplyUpvote(replyId), onSuccess: (payload, variables) => { updateReply(variables.replyId, payload); return refresh(); } });
  const markBest = useMutation({ mutationFn: ({ replyId }) => markBestDoubtSolution({ doubtId, replyId }), onSuccess: refresh });
  const report = useMutation({ mutationFn: ({ reportReasonId, description }) => reportDoubt({ doubtId, reportReasonId, description }), onSuccess: refresh });
  const answer = useMutation({ mutationFn: ({ content, attachments }) => addDoubtAnswer({ doubtId, content, attachments }), onSuccess: refresh });
  const editAnswer = useMutation({ mutationFn: ({ answerId, content, attachments, deleteAttachmentIds }) => editDoubtAnswer({ answerId, content, attachments, deleteAttachmentIds }), onSuccess: refresh });
  const reply = useMutation({ mutationFn: ({ parentReplyId, content, attachments }) => replyToDoubtAnswer({ doubtId, parentReplyId, content, attachments }), onSuccess: refresh });

  return {
    upvoteDoubt,
    bookmarkDoubt,
    toggleMyDoubt: toggleMyDoubtMutation,
    blockDoubt: blockDoubtMutation,
    upvoteReply,
    markBest,
    report,
    answer,
    editAnswer,
    reply,
  };
}

export function useDoubtListActions() {
  const queryClient = useQueryClient();
  const updateListItem = (doubtId, payload = {}) => {
    const result = payload?.doubt || payload?.data || payload;
    const isUpvoted = result?.is_upvoted;
    const totalUpvotes = result?.total_upvotes ?? result?.upvotes ?? result?.upvote_count;
    const bookmarkStatus = result?.bookmark_status ?? result?.is_bookmarked;
    
    return queryClient.setQueriesData({ queryKey: ['doubts'] }, (current) => {
      if (!current) return current;
      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          doubts: (page?.doubts || []).map((item) => {
            if (Number(item?.doubt_id || item?.id) === Number(doubtId)) {
              const currentItemUpvotes = Number(item?.total_upvotes || item?.upvotes_count || item?.upvote_count || item?.upvotes || 0);
              const newUpvoteCount = totalUpvotes ?? (isUpvoted != null ? currentItemUpvotes + (Number(isUpvoted) === 1 ? 1 : -1) : currentItemUpvotes);
              const computedUpvotes = Math.max(0, newUpvoteCount);
              return { 
                ...item, 
                ...(isUpvoted != null ? { is_upvoted: isUpvoted, upvotes: computedUpvotes } : {}), 
                ...(bookmarkStatus != null ? { is_bookmarked: bookmarkStatus } : {}) 
              };
            }
            return item;
          })
        })),
      };
    });
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['doubts'] });
  const upvote = useMutation({ mutationFn: ({ doubtId }) => toggleDoubtUpvote(doubtId), onSuccess: (payload, variables) => { updateListItem(variables.doubtId, payload); return refresh(); } });
  const bookmark = useMutation({ mutationFn: ({ doubtId }) => toggleDoubtBookmark(doubtId), onSuccess: (payload, variables) => { updateListItem(variables.doubtId, payload); return refresh(); } });
  return { upvote, bookmark };
}
